// De SSE-events van de workspace-chat vertaald naar viewdata.
//
// De events komen uit een plain Django view en zijn snake_case; overal elders in
// de frontend is het camelCase. Dat verschil hoort op één plek te bestaan, en
// dit is die plek: componenten krijgen getypeerde waarden en zien nooit een
// ruwe payload. De functies hier zijn puur, zodat ze zonder DOM te testen zijn.

import {
  ActionRun,
  FilterSuggestion,
  ReportProposal,
  ResearchProposal,
  SkillField,
  SkillRun,
} from "./types";

// Ruimte voor latere veilige toolstatussen ("bron zoeken", "document lezen").
// De server stuurt dit veld nog niet; het contract staat er alvast zodat een
// volgende MR alleen een event hoeft toe te voegen.
export interface ChatStatus {
  kind: string;
  label?: string;
}

export type ChatEvent =
  | { type: "session"; sessionId: number; assistantMessageId?: number; title?: string }
  | { type: "content"; text: string }
  | { type: "filter"; filter: FilterSuggestion }
  | { type: "researchProposal"; proposal: ResearchProposal }
  | { type: "reportProposal"; proposal: ReportProposal }
  | { type: "skillRun"; run: SkillRun }
  | { type: "assistantDeleted" }
  | { type: "status"; status: ChatStatus };

const skillField = (field: any): SkillField => ({
  name: field.name,
  label: field.label,
  type: field.type,
  required: Boolean(field.required),
  default: field.default,
  options: field.options ?? [],
  description: field.description ?? "",
  maxLength: field.max_length,
  sets: field.sets ?? {},
});

const actionRun = (item: any): ActionRun => ({
  id: item.id,
  action: item.action,
  label: item.label,
  paid: Boolean(item.paid),
  status: item.status,
  arguments: item.arguments ?? {},
  costEur: item.cost_eur,
  result: item.result ?? {},
  filterId: item.filter_id,
  researchId: item.research_id,
  reportId: item.report_id,
  fileName: item.file_name,
  fileUrl: item.file_url,
  error: item.error ?? "",
});

const skillRun = (run: any): SkillRun => ({
  id: run.id,
  messageId: run.message_id,
  skill: run.skill,
  label: run.label,
  fields: (run.fields ?? []).map(skillField),
  values: run.values ?? {},
  status: run.status,
  error: run.error ?? "",
  turn: Boolean(run.turn),
  organization: Boolean(run.organization),
  paid: Boolean(run.paid),
  actions: (run.actions ?? []).map(actionRun),
  filterId: run.filter_id,
  researchProposalId: run.research_proposal_id,
  reportId: run.report_id,
  created: run.created,
});

/**
 * Eén ruwe payload naar een getypeerd event, of `undefined` als we hem niet
 * kennen. Een onbekend eventtype is geen fout: de server mag er een toevoegen
 * zonder dat een oudere frontend de sessie laat kantelen.
 */
export function normaliseEvent(data: any): ChatEvent | undefined {
  if (!data || typeof data !== "object") return undefined;
  if (data.session_id) {
    return {
      type: "session",
      sessionId: data.session_id,
      assistantMessageId: data.assistant_message_id,
      title: data.title,
    };
  }
  if (data.content) return { type: "content", text: data.content };
  if (data.filter) {
    return {
      type: "filter",
      filter: {
        id: data.filter.id,
        criteria: data.filter.criteria,
        label: data.filter.label,
        total: data.filter.total,
        capped: data.filter.capped,
        created: data.filter.created,
      },
    };
  }
  if (data.research_proposal) {
    return {
      type: "researchProposal",
      proposal: {
        id: data.research_proposal.id,
        parcelId: data.research_proposal.parcel_id,
        parcelName: data.research_proposal.parcel_name,
        extraQuestion: data.research_proposal.extra_question,
        researchId: data.research_proposal.research_id,
        created: data.research_proposal.created,
      },
    };
  }
  if (data.report_proposal) {
    return {
      type: "reportProposal",
      proposal: {
        id: data.report_proposal.id,
        title: data.report_proposal.title,
        situation: data.report_proposal.situation,
        conclusion: data.report_proposal.conclusion,
        fileId: data.report_proposal.file_id,
        fileUrl: data.report_proposal.file_url,
        created: data.report_proposal.created,
      },
    };
  }
  if (data.skill_run) return { type: "skillRun", run: skillRun(data.skill_run) };
  if (data.assistant_deleted) return { type: "assistantDeleted" };
  if (data.status?.kind) {
    return { type: "status", status: { kind: data.status.kind, label: data.status.label } };
  }
  return undefined;
}

export interface ChatStream {
  sessionId?: number;
  assistantMessageId?: number;
  content: string;
  filters: FilterSuggestion[];
  researchProposals: ResearchProposal[];
  reportProposals: ReportProposal[];
  skillRuns: SkillRun[];
  deleted: boolean;
  finished: boolean;
  status?: ChatStatus;
}

export const emptyChatStream = (): ChatStream => ({
  content: "",
  filters: [],
  researchProposals: [],
  reportProposals: [],
  skillRuns: [],
  deleted: false,
  finished: true,
});

/** Eén event toepassen; een onbekend event laat de stroom ongemoeid. */
export function applyChatEvent(stream: ChatStream, data: any): ChatStream {
  const event = normaliseEvent(data);
  if (!event) return stream;
  switch (event.type) {
    case "session":
      return {
        ...stream,
        sessionId: event.sessionId,
        assistantMessageId: event.assistantMessageId,
      };
    case "content": {
      // Tekst die binnenkomt ís het bewijs dat de tool klaar is, dus de status
      // verdwijnt hier. Zonder dit blijft "Projectbronnen doorzoeken" staan
      // zodra iemand hem gaat tonen -- de server stuurt geen afsluitend event,
      // en dat hoeft ook niet.
      return { ...stream, content: stream.content + event.text, status: undefined };
    }
    case "filter":
      return { ...stream, filters: [...stream.filters, event.filter] };
    case "researchProposal":
      return { ...stream, researchProposals: [...stream.researchProposals, event.proposal] };
    case "reportProposal":
      return { ...stream, reportProposals: [...stream.reportProposals, event.proposal] };
    case "skillRun":
      return { ...stream, skillRuns: [...stream.skillRuns, event.run] };
    case "assistantDeleted":
      return { ...stream, deleted: true };
    case "status":
      return { ...stream, status: event.status };
  }
}

/**
 * De gebruiker drukte op stop, of de verbinding viel weg. Wat er al binnen was
 * blijft staan: de server bewaart hetzelfde gedeeltelijke antwoord met
 * `finished=False`, en het weggooien zou het gesprek anders maken dan de
 * historie die na een refresh terugkomt.
 */
export const stopChatStream = (stream: ChatStream): ChatStream => ({
  ...stream,
  finished: false,
});

/** Een mislukte stream: liever het halve antwoord dan een lege bel. */
export const failChatStream = (stream: ChatStream, message: string): ChatStream => ({
  ...stream,
  content: stream.content || message,
});
